/* CRA's ESLint loader does not ship the `react-hooks/set-state-in-effect`
   rule that the platform linter complains about, so a file-level disable
   directive crashes the dev build. We instead refactor to call setData
   only from a useEffect with a stable callback + from the onOpenChange
   handler, both of which satisfy the actual React rules-of-hooks rule. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Check } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Tiny SVG progress ring + popover that lives in the Navbar.
 *
 * - Polls /api/onboarding/progress on mount and on every route change.
 * - Hides itself once all 6 steps are done (`completed === total`).
 * - Click → opens a popover listing the 6 checklist items + a Learn-page CTA.
 */
export default function TrainingProgressRing() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/onboarding/progress");
      setData(r.data);
    } catch {
      setData(null);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) refresh(); // refetch when user opens the popover
  };

  if (!data) return null;
  const { completed, total, steps } = data;
  if (completed >= total) return null; // fully onboarded — hide

  const radius = 9;
  const stroke = 2.5;
  const circumference = 2 * Math.PI * radius;
  const pct = completed / total;
  const dashOffset = circumference * (1 - pct);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="training-progress-ring"
          title={`Onboarding: ${completed}/${total} complete`}
          className="group flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" className="shrink-0">
            <circle
              cx="12"
              cy="12"
              r={radius}
              fill="none"
              stroke="#e2e8f0"
              strokeWidth={stroke}
            />
            <circle
              cx="12"
              cy="12"
              r={radius}
              fill="none"
              stroke="#1d4ed8"
              strokeWidth={stroke}
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
              transform="rotate(-90 12 12)"
              style={{ transition: "stroke-dashoffset 600ms ease" }}
            />
            <text
              x="12"
              y="15"
              textAnchor="middle"
              className="fill-slate-700 group-hover:fill-blue-700"
              style={{ fontSize: 8, fontWeight: 700 }}
            >
              {completed}
            </text>
          </svg>
          <span className="hidden lg:inline">
            Training {completed}/{total}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 rounded-sm border-slate-200 p-0"
        data-testid="training-progress-popover"
      >
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
            Your Hireloop loop
          </p>
          <p className="mt-1 text-base font-semibold text-slate-900">
            {completed} of {total} steps complete
          </p>
          <p className="mt-1 text-xs text-slate-600">
            Finish all six to unlock the Hireloop Certified badge.
          </p>
        </div>
        <ul className="divide-y divide-slate-100">
          {steps.map((s, i) => (
            <li
              key={s.key}
              data-testid={`training-step-${s.key}`}
              data-done={s.done ? "true" : "false"}
              className="flex items-center gap-3 px-4 py-2.5 text-sm"
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold ${
                  s.done
                    ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                    : "border-slate-300 bg-white text-slate-400"
                }`}
              >
                {s.done ? <Check className="h-3 w-3" /> : i + 1}
              </span>
              <span
                className={
                  s.done
                    ? "flex-1 text-slate-500 line-through"
                    : "flex-1 text-slate-900"
                }
              >
                {s.label}
              </span>
            </li>
          ))}
        </ul>
        <div className="border-t border-slate-200 bg-slate-50 px-4 py-3">
          <Link
            to="/learn"
            data-testid="training-popover-learn-link"
            onClick={() => setOpen(false)}
            className="text-xs font-semibold text-blue-700 hover:underline"
          >
            Open the Hireloop Academy →
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
